import { keepPreviousData, useQuery, UseQueryResult } from '@tanstack/react-query';
import { api } from 'src/constants/apiRoutes';
import { queryKeys } from 'src/constants/querykeys';
import { mapSearchProductsResponse } from 'src/sections/search_products/searchProductMapper';
import { SearchResultProduct } from 'src/sections/search_products/searchProductData';
import {
  SearchProductsRequest,
  SearchProductsResponse,
} from 'src/types/search_products/SearchProducts';
import axiosInstance from 'src/utils/axios';

export interface SearchProductsQueryResult {
  totalRecords: number;
  items: SearchResultProduct[];
}

interface UseSearchProductsQueryOptions {
  request: SearchProductsRequest;
  enabled?: boolean;
}

export const buildSearchProductsUrl = (request: SearchProductsRequest): string => {
  const refineText = request.refineText?.trim();
  return refineText
    ? `${api.searchProducts}?refineText=${encodeURIComponent(refineText)}`
    : api.searchProducts;
};

export const isSearchProductsQueryEnabled = (
  request: SearchProductsRequest,
  enabled = true
): boolean => enabled && Boolean(request.searchText.trim());

async function fetchSearchProducts(request: SearchProductsRequest): Promise<SearchProductsResponse> {
  const response = await axiosInstance.post<SearchProductsResponse>(buildSearchProductsUrl(request), {
    searchText: request.searchText,
    queryOptions: request.queryOptions,
  });
  // TODO: Remove — temporary override so isNoItem can be tested before API returns the flag.
  // return {
  //   ...response.data,
  //   items: (response.data.items ?? []).map((item) => ({ ...item, isNoItem: true })),
  // };
  return response.data;
}

export const useSearchProductsQuery = ({
  request,
  enabled = true,
}: UseSearchProductsQueryOptions): UseQueryResult<SearchProductsQueryResult> =>
  useQuery<SearchProductsResponse, Error, SearchProductsQueryResult>({
    queryKey: queryKeys.searchProducts.list(request),
    queryFn: () => fetchSearchProducts(request),
    enabled: isSearchProductsQueryEnabled(request, enabled),
    placeholderData: keepPreviousData,
    select: mapSearchProductsResponse,
  });
